// src/components/SharedComponents/ImageFileDrop.js
// @flow

import React, { useCallback, useMemo, useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useDropzone } from 'react-dropzone';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import { useTheme } from '@mui/material/styles';

import imageFileDropStyles from './Styles/ImageFileDrop.Style';
import SPBackend from '../../services/SPBackend';
import { log } from '../../utils/jsUtils';
import { setSnackMessage } from '../../reduxStore/modules/common';
import { selectUser } from '../../reduxStore/modules/user/selectors';
import { setTenantEnableUpload } from '../../reduxStore/modules/tenants/actions';
import { createClasses } from '../../utils/stylesUtils';

import type { Node } from 'react';

// The highlight styles for "active/accept/reject"
const activeStyle = { borderColor: '#2196f3' };
const acceptStyle = { borderColor: '#00e676' };
const rejectStyle = { borderColor: '#ff1744' };

type Props = {
  onImageReceived?: (attachment: { Url: string, MimeType: string }) => void,
  children?: Node
};

function ImageFileDrop(props: Props) {
  const { onImageReceived, children } = props;

  const dispatch = useDispatch();
  const user = useSelector(selectUser);
  const tenantEnableUpload = useSelector(state => state.tenants?.tenantEnableUpload);

  const theme = useTheme();
  const classes = createClasses(imageFileDropStyles, theme);

  // Local state
  const [fileDropSuccess, setFileDropSuccess] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [acceptedFile, setAcceptedFile] = useState(null);

  const handleHideDropZone = () => {
    dispatch(setTenantEnableUpload(false));
  };

  // onDrop callback — read the file or upload to server
  const onDrop = useCallback(
    (addedFiles) => {
      if (addedFiles.length === 0) return;
      const file = addedFiles[0];
      if (file.size > 5000000) {
        dispatch(setSnackMessage({
          message: 'File exceeds maximum size (5MB).',
          level: 'error',
          duration: 5000
        }));
        return;
      }
      setAcceptedFile(file);
    },
    [dispatch]
  );

  // Watch acceptedFile, call SPBackend to upload
  useEffect(() => {
    if (!acceptedFile) return;
    setUploading(true);
    SPBackend.uploadConversationImage(acceptedFile, user)
      .then((response) => {
        setUploading(false);
        setAcceptedFile(null);
        const { data } = response;
        log('[SPBackend.uploadConversationImage] response: ', data);
        setFileDropSuccess(true);
        // If parent gave a callback
        if (typeof onImageReceived === 'function') {
          onImageReceived(data.attachment);
        }
      })
      .catch((err) => {
        setUploading(false);
        setAcceptedFile(null);
        log('[uploadConversationImage] error: ', err);
        dispatch(setSnackMessage({
          message: 'Upload failed.',
          level: 'error',
          duration: 5000
        }));
      });
  // eslint-disable-next-line
  }, [acceptedFile, user, dispatch]);

  // Use dropzone
  const {
    getRootProps,
    getInputProps,
    isDragActive,
    isDragAccept,
    isDragReject
  } = useDropzone({
    multiple: false,
    onDrop,
    accept: { 'image/*': [] }
  });

  // Condition: if children are provided, we skip the dashed border style
  const baseStyle = useMemo(() => {
    if (!children) {
      // "no children" => apply the dash/border
      return classes.dropZone;
    }
    // "children exist" => apply minimal or no styling
    return {};
  }, [children, classes.dropZone]);

  // Merge the highlight states
  const combinedStyle = useMemo(() => {
    return {
      ...baseStyle,
      ...(isDragActive ? activeStyle : {}),
      ...(isDragAccept ? acceptStyle : {}),
      ...(isDragReject ? rejectStyle : {}),
    };
  }, [baseStyle, isDragActive, isDragAccept, isDragReject]);

  // If you want to hide the drop zone entirely under some condition:
  // if (!tenantEnableUpload) return null;

  return (
    <Box sx={classes.root}>
      <Box {...getRootProps()} sx={{ ...classes.wrapper, ...combinedStyle }}>
        <input {...getInputProps()} />
        {children ? (
          // If we have children (like a FAB), show that
          <>
            {children}
            {uploading && (
              <CircularProgress size={24} sx={classes.fabProgress} />
            )}
          </>
        ) : (
        // If no children => old "drag & drop" UI
        <>
          <Box sx={classes.iconCtr} component="span">
            <AddPhotoAlternateIcon color="action" />
          </Box>
          <Typography sx={classes.dropZoneText}>
            {isDragActive
              ? 'Drop the file here ...'
              : 'Drag & drop an image, or click to select one'
            }
          </Typography>
          {uploading && <CircularProgress size={24} />}
        </>
      )}
      </Box>
    </Box>
  );
}

export default ImageFileDrop;